import { useMemo } from "react";
import {
  Group,
  Stack,
  Text,
  Badge,
  CloseButton,
  Flex,
  SegmentedControl,
  Center,
} from "@mantine/core";
import { IconList, IconMap } from "@tabler/icons-react";
import type { ApartmentSearchParams, FilterOptions, SortField, SortOrder } from "@/api/types";
import { ApartmentSearch } from "./ApartmentSearch";
import { ApartmentSort } from "./ApartmentSort";
import { ApartmentFilters } from "./ApartmentFilters";

export type ViewMode = "list" | "map";

interface ApartmentToolbarProps {
  searchParams: ApartmentSearchParams;
  filterOptions: FilterOptions | undefined;
  total: number;
  view: ViewMode;
  onViewChange: (view: ViewMode) => void;
  onSearchChange: (search: string) => void;
  onSortChange: (sortBy: SortField, sortOrder: SortOrder) => void;
  onFilterChange: (filters: Partial<ApartmentSearchParams>) => void;
  onClearFilters: () => void;
}

export function ApartmentToolbar({
  searchParams,
  filterOptions,
  total,
  view,
  onViewChange,
  onSearchChange,
  onSortChange,
  onFilterChange,
  onClearFilters,
}: ApartmentToolbarProps) {
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchParams.min_rent !== undefined) count++;
    if (searchParams.max_rent !== undefined) count++;
    if (searchParams.min_size !== undefined) count++;
    if (searchParams.max_size !== undefined) count++;
    if (searchParams.min_rooms !== undefined) count++;
    if (searchParams.max_rooms !== undefined) count++;
    if (searchParams.city) count++;
    if (searchParams.area) count++;
    if (searchParams.available_from) count++;
    if (searchParams.available_until) count++;
    return count;
  }, [searchParams]);

  const activeFilters = useMemo(() => {
    const filters: { key: string; label: string }[] = [];
    if (searchParams.min_rent !== undefined)
      filters.push({ key: "min_rent", label: `Min ${searchParams.min_rent} kr` });
    if (searchParams.max_rent !== undefined)
      filters.push({ key: "max_rent", label: `Max ${searchParams.max_rent} kr` });
    if (searchParams.min_size !== undefined)
      filters.push({ key: "min_size", label: `Min ${searchParams.min_size} m²` });
    if (searchParams.max_size !== undefined)
      filters.push({ key: "max_size", label: `Max ${searchParams.max_size} m²` });
    if (searchParams.min_rooms !== undefined)
      filters.push({ key: "min_rooms", label: `Min ${searchParams.min_rooms} rooms` });
    if (searchParams.max_rooms !== undefined)
      filters.push({ key: "max_rooms", label: `Max ${searchParams.max_rooms} rooms` });
    if (searchParams.city) filters.push({ key: "city", label: searchParams.city });
    if (searchParams.area) filters.push({ key: "area", label: searchParams.area });
    if (searchParams.available_from)
      filters.push({ key: "available_from", label: `From ${searchParams.available_from}` });
    if (searchParams.available_until)
      filters.push({ key: "available_until", label: `Until ${searchParams.available_until}` });
    return filters;
  }, [searchParams]);

  const removeFilter = (key: string) => {
    onFilterChange({ [key]: undefined });
  };

  return (
    <Stack gap="md">
      <Flex direction={{ base: "column", sm: "row" }} gap="sm">
        <ApartmentSearch value={searchParams.search ?? ""} onChange={onSearchChange} />
        <Group gap="sm">
          <ApartmentSort
            sortBy={searchParams.sort_by ?? "first_seen"}
            sortOrder={searchParams.sort_order ?? "desc"}
            onChange={onSortChange}
          />
          <ApartmentFilters
            filters={searchParams}
            filterOptions={filterOptions}
            onChange={onFilterChange}
            onClearAll={onClearFilters}
            activeFilterCount={activeFilterCount}
          />
          <SegmentedControl
            value={view}
            onChange={(v) => onViewChange(v as ViewMode)}
            data={[
              {
                value: "list",
                label: (
                  <Center style={{ gap: 4 }}>
                    <IconList size={16} />
                  </Center>
                ),
              },
              {
                value: "map",
                label: (
                  <Center style={{ gap: 4 }}>
                    <IconMap size={16} />
                  </Center>
                ),
              },
            ]}
            styles={{
              root: { backgroundColor: "#0f3460" },
              indicator: { backgroundColor: "#e94560" },
            }}
          />
        </Group>
      </Flex>

      <Group gap="sm" wrap="wrap">
        <Text size="sm" c="dimmed">
          {total} apartments
        </Text>
        {activeFilters.length > 0 && (
          <>
            <Text c="dimmed">|</Text>
            {activeFilters.map((filter) => (
              <Badge
                key={filter.key}
                variant="filled"
                color="#0f3460"
                size="sm"
                rightSection={
                  <CloseButton
                    size="xs"
                    onClick={() => removeFilter(filter.key)}
                    variant="transparent"
                    c="dimmed"
                  />
                }
              >
                {filter.label}
              </Badge>
            ))}
          </>
        )}
      </Group>
    </Stack>
  );
}
